import CalendarView from '../Calendar'
import EventMap from '../../Components/EventMap';

export default class List extends CalendarView {
    constructor(calendar, identifier) {
        super(calendar, identifier)
        this.unit = 'day'
        this.majorUnit = 'week'
        this.viewName = 'list'
        this.state.collapsed = false
    }

    get startDate() {
        return this._startDate ? this._startDate.clone().startOf(this.majorUnit) : undefined
    }

    set startDate(startDate) {
        if (!moment.isMoment(startDate) || !startDate.isValid()) {
            throw Error(`${startDate} is not a valid moment instance!`)
        }
        if (this._endDate && this._endDate.unix() < startDate.unix()) {
            throw Error(`Start Date must be earlier than the supplied End Date: ${this.endDate.format('YYYY-MM-DD  HH:mm')}!`)
        }
        this._startDate = startDate.clone()
    }

    addToDOM() {
        // Build the eventMap, creates the container div, adds calendar controls
        if (!this.unit) {
            this.unit = 'day'
        }
        if (this.startDate === undefined) {
            this.setStartDateToDefault()
        }
        if (this.endDate === undefined) {
            this.setEndDateToDefault()
        } else {
            // adjust endDate to fill the view
            this.endDate = this.endDate.clone().endOf(this.majorUnit)
        }
        this.state.eventMap = new EventMap(this.getEventMapKeyFormat(), this.calendar.events);
        this.view.append(this.getControls())
        // add List View specific html
        const calendarList = document.createElement('div')
        calendarList.className = 'whale-calendar-list'

        this.view.append(calendarList)
        this.render()

        this.view.append(this.editModal.view)
    }

    render() {
        const calendarList = this.view.querySelector('.whale-calendar-list')
        calendarList.innerHTML = ''
        let date = this.startDate.clone().startOf(this.majorUnit).startOf(this.unit)
        let nowDateKey = this.state.eventMap.getKeyFromMoment(moment())
        while (date < this.endDate) {
            const majorUnitRow = this.getMajorUnitRow()
            calendarList.append(majorUnitRow)

            const majorUnitHeader = this.getMajorUnitHeader(date)
            majorUnitRow.append(majorUnitHeader)

            const majorUnitCol = this.getMajorUnitCol()
            majorUnitRow.append(majorUnitCol)

            const majorEnd = date.clone().endOf(this.majorUnit)
            date.startOf(this.unit)
            while (date < majorEnd) {
                // Contains one unit of time
                const key = this.state.eventMap.getKeyFromMoment(date)
                const unitRow = document.createElement('div')
                unitRow.className = 'row whale-unit-row'
                unitRow.setAttribute('data-date-key', key)
                if (this.state.collapsed && 
                    (!this.state.eventMap[key] || 
                     this.state.eventMap[key].length === 0 ||
                     unitRow.querySelectorAll('.whale-event-row').length === 0)
                ) {
                    unitRow.classList.add('collapsed')
                }
                majorUnitCol.append(unitRow)

                const unitCol = this.getUnitCol(date)
                unitRow.append(unitCol)

                const eventsCol = this.getEventsCol(key)
                if (key === nowDateKey) {
                    eventsCol.classList.add('today')
                }

                unitRow.append(eventsCol)

                this.updateUiForDateKey(key)
                date.add(1, this.unit)
            }
        }

        this.addEventListeners()
    }

    updateUiForDateKey(dateKey) {
        // First call the parent method
        super.updateUiForDateKey(dateKey)
        // only if state is collapsed make some adjustments based on the filters
        if (this.state.collapsed) {
            const unitRow = this.view.querySelector(`.whale-unit-row[data-date-key="${dateKey}"]`)
            if (unitRow && unitRow.className.includes('collapsed') && unitRow.querySelectorAll('.whale-event-row').length > 0) {
                unitRow.classList.remove('collapsed')
            } else if (unitRow && !unitRow.className.includes('collapsed') && unitRow.querySelectorAll('.whale-event-row').length == 0) {
                unitRow.classList.add('collapsed')
            }
        }
    }

    updateAllEvents() {
        let date = this.startDate.startOf(this.majorUnit).startOf(this.unit)
        const end = this.endDate
        while (date <= end) {
            this.updateUiForDateKey(this.state.eventMap.getKeyFromMoment(date))
            date.add(1, this.unit)
        }
    }

    getMajorUnitRow() {
        // A row displaying the Major Unit, Contains micro units (Ex: Week containing Days, Day containing Hours)
        const majorUnitRow = document.createElement('div')
        majorUnitRow.className = 'row whale-major-unit-row'
        return majorUnitRow
    }

    getMajorUnitHeader(start) {
        if (!moment.isMoment(start)) {
            throw Error('Argument must be a moment object!')
        }
        // Displays the major unit (Ex: 2019, January, Sunday)
        const majorUnitHeader = document.createElement('h3')
        majorUnitHeader.className = 'whale-major-unit-header'
        // Text is dependent on the units being displayed
        let text
        switch (this.majorUnit) {
            case 'day':
                text = start.format('MM/DD/YY')
                break
            case 'week':
                const end = start.clone().endOf('week')
                text = `${start.format('MM/DD/YY')} - ${end.format('MM/DD/YY')}`
                break
            case 'month':
                text = start.format('MMMM, YYYY')
                break
            case 'year':
                text = start.format('YYYY')
                break
            default: 
                throw Error('You must select your units for the View')
        }
        majorUnitHeader.textContent = text
        return majorUnitHeader
    }

    getMajorUnitCol() {
        // Contains the unit rows (Ex: week, day, hour)
        const majorUnitCol = document.createElement('div')
        majorUnitCol.className = 'col-xs-12 whale-major-unit-col'
        return majorUnitCol
    }

    getUnitCol(date) {
        if (!moment.isMoment(date)) {
            throw Error('Argument must be a moment object!')
        }
        // Displays the unit of time (Ex: Jan; 1/1 - 1/7, Fri, 1/6/19, 12PM)
        const unitCol = document.createElement('div')
        unitCol.className = 'col-xs-3 whale-unit-col'
        switch (this.unit) {
            case 'hour':
                const hourSpan = document.createElement('span')
                hourSpan.textContent = date.format('h A')
                unitCol.append(hourSpan)
                break
            case 'day':
                const daySpan = document.createElement('span')
                daySpan.textContent = date.format('ddd')
                unitCol.append(daySpan)

                const br = document.createElement('br')
                unitCol.append(br)

                const dateSpan = document.createElement('span')
                dateSpan.textContent = date.format('M/D')
                unitCol.append(dateSpan)
                break
            case 'week':
                const weekSpan = document.createElement('span')
                const end = date.clone().endOf('week')
                weekSpan.textContent = date.format(`${date.format('MM/DD/YY')} - ${end.format('MM/DD/YY')}`)
                unitCol.append(weekSpan)
                break
            case 'month':
                const monthSpan = document.createElement('span')
                monthSpan.textContent = date.format('MMM')
                unitCol.append(monthSpan)
                break
            default: 
                throw Error('You must select your units for the View')
        }
        return unitCol
    }

    getEventsCol(dateKey) {
        // Contains the events 
        const eventsCol = document.createElement('div')
        eventsCol.className = 'col-xs-9 whale-events-col whale-events-droppable'
        eventsCol.setAttribute('data-date-key', dateKey)
        return eventsCol
    }

    // overwriting getControls to add a collapse button
    getControls() {
        // Displays the Range of dates being shown
        const controls = document.createElement('div')
        controls.className = 'whale-controls'
        // Displays the current view's start and end (Ex: 2019, January, Sunday)
        const header = this.getHeader()
        controls.append(header)

        const buttonContainer = document.createElement('div')
        buttonContainer.className = 'whale-controls-btn-container'

        buttonContainer.append(this.getCollapseButton())    
        buttonContainer.append(this.getPreviousButton(header))
        buttonContainer.append(this.getNextButton(header))

        controls.append(buttonContainer)

        return controls
    }

    getCollapseButton() {
        const collapseButton = document.createElement('button')
        collapseButton.className = 'whale-calendar-collapse-btn whale-controls-btn'
        collapseButton.textContent = 'Collapse'
        collapseButton.addEventListener('click', (e) => {
            this.toggleCollapse()
            if (this.state.collapsed) {
                collapseButton.textContent = 'Expand'
                collapseButton.classList.add('active')
            } else {
                collapseButton.textContent = 'Collapse'
                collapseButton.classList.remove('active')
            }
        })
        return collapseButton
    }

    toggleCollapse() {
        if (this.state.collapsed) {
            this.view.querySelectorAll('.whale-unit-row.collapsed').forEach((element) => {
                element.classList.remove('collapsed')
            })
        } else {
            this.view.querySelectorAll('.whale-unit-row').forEach((element) => {
                const dateKey = element.getAttribute('data-date-key')
                if (!this.state.eventMap[dateKey] || 
                    this.state.eventMap[dateKey].length === 0 ||
                    element.querySelectorAll('.whale-event-row').length === 0
                ) {
                    element.classList.add('collapsed')
                }
            })
        }
        this.state.collapsed = !this.state.collapsed
    }

}